import { Trans } from '@lingui/react'
import {
  IconBarcode,
  IconCaretDown,
  IconDelivery,
  IconPills,
  IconTag,
} from '@redteclab/icons'
import { clsx } from 'clsx'
import { useMemo, type VFC } from 'react'

import type { StockStatus } from 'server/utils/pimServiceUtilsTypes' // eslint-disable-line no-restricted-imports -- only type import
import { GTM_EVENT_CLICK_PRODUCT, PRODUCT_STATUS_NL } from 'shared/consts'
import {
  isMarketplace,
  isVariantAttributes,
} from 'shared/experiments/utils/featureFlags'
import { type AdServerMetadata } from 'types/adServerTypes'
import DiscountBadge from 'views/components/atoms/DiscountBadge'
import SvgIcon from 'views/components/atoms/Icon/SvgIcon'
import StarRating from 'views/components/molecules/StarRating'
import CompactProductCardVariant from 'views/components/organisms/FilteredProductList/CompactProductListItem/CompactProductCardVariant'
import CompactProductListItemImage from 'views/components/organisms/FilteredProductList/CompactProductListItem/CompactProductListItemImage'
import CompactProductListItemPrice from 'views/components/organisms/FilteredProductList/CompactProductListItem/CompactProductListItemPrice'
import CompactProductListItemVariantList from 'views/components/organisms/FilteredProductList/CompactProductListItem/CompactProductListItemVariantList'
import {
  collectLabelsProps,
  shouldRenderVariants,
} from 'views/components/organisms/FilteredProductList/SearchProductListItem/helpers'
import { genLabelAndIdPair } from 'views/components/organisms/FilteredProductList/SearchProductListItem/IdSwitcher'
import SearchProductLabels from 'views/components/organisms/FilteredProductList/SearchProductListItem/SearchProductLabels'
import { useProductUrl } from 'views/components/organisms/FilteredProductList/useProductUrl'
import useVariantState from 'views/components/organisms/FilteredProductList/useVariantState'
import ProductStatus from 'views/components/organisms/ProductStatus'
import useShopAndRuntimeConfig from 'views/enhancers/useShopAndRuntimeConfig'
import { getDiscountToShow } from 'views/utils/discount'
import { getStatusColorRebranding } from 'views/utils/productStatusUtils'
import {
  isDiscountValid,
  isSingleVariantAttributeSERP,
} from 'views/utils/productUtils'

import './CompactProductListItem.scss'

export type Variant = {
  id: string
  stockStatus: StockStatus
  unit: string
  unitQuantityString: string
}

export type Product = {
  flags: {
    prescriptionMedicine: boolean
  }
  rating: number
  ratingCount: number
  stockStatus: StockStatus
  variants: Variant[]
}

export type VariantState = {
  bestOffer?: {
    strike_price?: {
      formatted: string
    }
  }
  currentVariantId: string
  discount: number
  ean?: string
  imageUrl: string
  isbn?: string
  metadataId?: string
  name: string
  objectID: string
  offers: {
    count: number
  }
  offerTypeSae: boolean
  packageDetails: {
    id?: string
    manufacturer: string
    pharmaForm?: string
    unit: string
    unitQuantityString: string
  }
  priceInfo: {
    prices: unknown[]
    stockStatus?: StockStatus
    unitPricing: string
  }
  pzn?: string
  upid?: string
}

type CompactProductListItemProps = {
  adServerMetadata: AdServerMetadata
  product: Product
  queryParams: object
  enableSponsoredLabel: boolean
  showDiscountLabel: boolean
}

const CompactProductListItem: VFC<CompactProductListItemProps> = props => {
  const {
    adServerMetadata,
    product,
    queryParams,
    enableSponsoredLabel,
    showDiscountLabel,
  } = props

  const { publicRuntimeConfig } = useShopAndRuntimeConfig()

  const { pageProperties, publicConfig } = publicRuntimeConfig

  const { tenant, discountBoundaries } = publicConfig

  const { experiments, pageType } = pageProperties

  const variantAttributesEnabled = isVariantAttributes(experiments)

  const [variantState] = useVariantState<VariantState, Variant>(product)

  const getProductUrl = useProductUrl({
    adServerMetadata,
    product,
    queryParams,
    variantState,
  })

  const {
    flags: { prescriptionMedicine },
    rating,
    ratingCount,
    variants,
  } = product

  const {
    discount,
    ean,
    isbn,
    metadataId,
    objectID,
    packageDetails,
    priceInfo,
    pzn,
    upid,
    currentVariantId,
  } = variantState

  const stockStatus = priceInfo.stockStatus ?? product.stockStatus
  const currentVariant = variants.find(
    variant => variant.id === currentVariantId
  )

  const isValidDiscount = isDiscountValid(discount, discountBoundaries)

  const labelsProps = collectLabelsProps(
    {
      ...product,
      ...currentVariant,
      metadataId,
      objectID,
    },
    enableSponsoredLabel,
    publicRuntimeConfig,
    adServerMetadata,
    showDiscountLabel && isValidDiscount
  )

  const searchProductLabelsComponent = (
    <SearchProductLabels
      {...labelsProps}
      additionalClasses={['new-serp-exp']}
    />
  )

  const isItemWithMultipleVariantAttributes =
    variantAttributesEnabled &&
    !isSingleVariantAttributeSERP(product.variants) &&
    product.variants?.length > 1

  const shouldDisplayLinkToOfferPage =
    !isItemWithMultipleVariantAttributes &&
    variantState.offers.count > 1 &&
    isMarketplace(experiments, tenant)

  const showVariants = useMemo(() => {
    const shouldShowProductVariantsBox = () => {
      const shouldShowVariants = product.variants?.length > 1

      if (variantAttributesEnabled) {
        return (
          shouldShowVariants && isSingleVariantAttributeSERP(product.variants)
        )
      }

      return shouldShowVariants
    }

    return (
      !shouldDisplayLinkToOfferPage &&
      shouldShowProductVariantsBox() &&
      shouldRenderVariants(experiments, pageType)
    )
  }, [
    experiments,
    pageType,
    product.variants,
    shouldDisplayLinkToOfferPage,
    variantAttributesEnabled,
  ])

  return (
    <article className="o-CompactProductListItem">
      {searchProductLabelsComponent}
      <header className="o-CompactProductListItem__header">
        <div className="o-CompactProductListItem__image-wrapper">
          <CompactProductListItemImage
            alt={variantState.name}
            className={clsx('o-CompactProductListItem__image', {
              'o-CompactProductListItem__image--not-available':
                (stockStatus.status ?? true) ||
                stockStatus.status === PRODUCT_STATUS_NL,
            })}
            src={variantState.imageUrl}
          />
          {getDiscountToShow({ discount, tenant }) ? (
            <DiscountBadge
              className="o-CompactProductListItem__discount-badge"
              discount={discount}
            >
              -{discount}%
            </DiscountBadge>
          ) : null}
        </div>
        <div className="o-CompactProductListItem__title-wrapper">
          <a
            className="o-CompactProductListItem__title"
            href={getProductUrl(GTM_EVENT_CLICK_PRODUCT)}
          >
            {variantState.name}
          </a>
          {!prescriptionMedicine && ratingCount > 0 && (
            <StarRating
              className="o-CompactProductListItem__rating"
              hasSuperScript
              rating={rating}
              ratingCount={ratingCount}
              ratingCountColor="dark"
            />
          )}
          {showVariants && (
            <CompactProductListItemVariantList
              selectedVariantId={variantState.currentVariantId}
              variants={product.variants}
            />
          )}
          {isItemWithMultipleVariantAttributes && (
            <CompactProductCardVariant>
              <Trans id="product.multiple-variant-attributes.label" />
            </CompactProductCardVariant>
          )}
          {shouldDisplayLinkToOfferPage && (
            <CompactProductCardVariant>
              <Trans id="product.multiple-sellers.label" />
            </CompactProductCardVariant>
          )}
        </div>
      </header>
      <CompactProductListItemPrice
        className="o-CompactProductListItem__price"
        prescriptionMedicine={prescriptionMedicine}
        variantState={variantState}
      />
      <details className="o-CompactProductListItem__details">
        <summary className="o-CompactProductListItem__toggle">
          <SvgIcon
            className="o-CompactProductListItem__toggle-icon"
            component={IconCaretDown}
          />
        </summary>
        <div className="o-CompactProductListItem__details-content">
          <section className="o-CompactProductListItem__details-item">
            <IconDelivery
              className={clsx(
                'o-CompactProductListItem__details-icon',
                getStatusColorRebranding(stockStatus.statusColor)
              )}
            />
            <ProductStatus
              className="o-CompactProductListItem__details-text o-CompactProductListItem__stock-status"
              stockStatus={stockStatus}
            />
          </section>
          <section className="o-CompactProductListItem__details-item">
            <IconBarcode className="o-CompactProductListItem__details-icon" />
            <span className="o-CompactProductListItem__details-text a-break-long-words">
              {
                genLabelAndIdPair({
                  ean,
                  isbn,
                  pzn,
                  uid: '',
                  upid,
                }).id
              }
            </span>
          </section>
          {variantState.packageDetails.manufacturer && (
            <section className="o-CompactProductListItem__details-item">
              <IconTag className="o-CompactProductListItem__details-icon" />
              <span className="o-CompactProductListItem__details-text">
                {variantState.packageDetails.manufacturer}
              </span>
            </section>
          )}
          {!isItemWithMultipleVariantAttributes && (
            <section className="o-CompactProductListItem__details-item">
              <IconPills className="o-CompactProductListItem__details-icon" />
              <span className="o-CompactProductListItem__details-text">
                {packageDetails.unitQuantityString} {packageDetails.unit}
                {packageDetails.pharmaForm && (
                  <> | {packageDetails.pharmaForm}</>
                )}
              </span>
            </section>
          )}
        </div>
      </details>
    </article>
  )
}

export default CompactProductListItem
